<template>
  <span
    class="bdx-export-icon-btn ml-2"
    role="button"
    :title="isExporting ? 'Exporting…' : 'Download Excel report'"
    @click="!isExporting && handleExport()"
  >
    <span v-if="isExporting" class="bdx-btn-spinner" />
    <b-icon v-else icon="arrow-down" :font-scale="0.9" />
  </span>
</template>

<script>
import { defineComponent, inject, ref } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import * as XLSX from 'xlsx'

import { LincolnGlobalUMRService } from '@services'
import { useTryCatch, useToast, useCurrentUser, useUserCompany } from '@composables'
import { extractBdxRows } from '@util'

import { BaseIcon } from '@components/base'

import { BDXStatusPendingManagerInjectionKey } from './BDXStatusPendingManager.constants'

const LIST_COLUMNS = [
  { key: 'umr',              label: 'UMR' },
  { key: 'risk_code',        label: 'Risk Code' },
  { key: 'cat_code',         label: 'Catastrophe Code' },
  { key: 'section_no',       label: 'Section Number' },
  { key: 'lead_underwriter', label: 'Lead Underwriter' }
]

const PAID_MONTH_KEYS = [
  'Paid_This_Month_Indemnity',
  'Paid_This_Month_Fees',
  'Paid_this_month_Expenses',
  'Paid_this_month_Attorney_Coverage_Fees',
  'Paid_this_month_Adjusters_Fees',
  'Paid_this_month_Defence_Fees',
  'Paid_this_month_TPA_Fees',
  'Paid_this_month_Bank_Charges'
]

const RESERVE_KEYS = [
  'Reserve_Expenses',
  'Reserve_Attorney_Coverage_Fees',
  'Reserve_Adjusters_Fees',
  'Reserve_Defence_Fees',
  'Reserve_TPA_Fees',
  'Reserve_Indemnity',
  'Reserve_Fees',
  'Initial_Reserve_Value'
]

const PREVIEW_COLUMNS = [
  { key: 'UMR_Risk_Cat_Section', label: 'LimeIQ Key' },
  { key: 'Claim_Reference',      label: 'Claim Reference' },
  { key: 'Claim_Status',         label: 'Status' },
  { key: '_total_paid_month',    label: 'Total Paid This Month' },
  { key: '_total_reserve',       label: 'Total Reserve' },
]

const ALL_COLUMNS = [...LIST_COLUMNS, ...PREVIEW_COLUMNS]

export default defineComponent({
  name: 'BDXStatusPendingManagerExportButton',
  components: { BaseIcon },
  setup() {
    const { useGetters } = createVuexHelpers()
    const { bdxFilters } = useGetters(['bdxFilters'])

    const { userEmail, isSuperUser } = useCurrentUser()
    const { roleName: userCompanyRoleName, roleValue: userCompanyRoleValue } = useUserCompany()
    const { showToast } = useToast()

    const { search, columnFilters } = inject(BDXStatusPendingManagerInjectionKey)

    const isExporting = ref(false)

    const getCompanyRole = () => ({
      companyRole: isSuperUser.value
        ? bdxFilters.value.companyRoleName
        : userCompanyRoleName.value,
      companyRoleValue: isSuperUser.value
        ? bdxFilters.value.companyRoleValue
        : userCompanyRoleValue.value
    })

    // Local XLSX download — fetches all UMRs, calls preview API for each, exports combined data
    const downloadLocal = async (items) => {
      // Fetch preview rows for each UMR in batches of 10
      const BATCH = 10
      const allRows = []
      for (let i = 0; i < items.length; i += BATCH) {
        const batchResults = await Promise.all(
          items.slice(i, i + BATCH).map(async (item) => {
            try {
              const response = await LincolnGlobalUMRService.getBdxFullPreview({
                umr_rc_cc_sn: item.umr_rc_cc_sn || ''
              })
              const numVal = (row, k) => (typeof row[k] === 'number' ? row[k] : 0)
              const previewRows = extractBdxRows(response.data)
              if (!previewRows.length) {
                return [ALL_COLUMNS.map(({ key }) =>
                  LIST_COLUMNS.find((c) => c.key === key) ? (item[key] != null ? item[key] : '') : ''
                )]
              }
              return previewRows.map((row) =>
                ALL_COLUMNS.map(({ key }) => {
                  if (LIST_COLUMNS.find((c) => c.key === key)) return item[key] != null ? item[key] : ''
                  if (key === '_total_paid_month') return PAID_MONTH_KEYS.reduce((s, k) => s + numVal(row, k), 0)
                  if (key === '_total_reserve') return RESERVE_KEYS.reduce((s, k) => s + numVal(row, k), 0)
                  const v = row[key]
                  return v != null ? v : ''
                })
              )
            } catch {
              return [ALL_COLUMNS.map(({ key }) =>
                LIST_COLUMNS.find((c) => c.key === key) ? (item[key] != null ? item[key] : '') : 0
              )]
            }
          })
        )
        batchResults.forEach((rows) => allRows.push(...rows))
      }

      const headers = ALL_COLUMNS.map(({ label }) => label)
      const ws = XLSX.utils.aoa_to_sheet([headers, ...allRows])

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'BDX Status Pending')
      const now = new Date()
      const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`
      XLSX.writeFile(wb, `BDX Status Pending ${ts}.xlsx`)
    }

    // Email export — LincolnSmart generates Excel and LimeIQ emails it to the user
    const exportByEmail = async () => {
      const { companyRole, companyRoleValue } = getCompanyRole()

      await LincolnGlobalUMRService.exportInactive(
        companyRole,
        companyRoleValue,
        userEmail.value,
        { search: search.value, ...columnFilters.value }
      )

      showToast(
        'The Bordeaux Status Pending export is in process, and you will receive an update via email shortly!'
      )
    }

    const EXPORT_EMAIL_THRESHOLD = 100

    const handleExport = async () => {
      isExporting.value = true

      await useTryCatch(async () => {
        const { companyRole, companyRoleValue } = getCompanyRole()
        const { data } = await LincolnGlobalUMRService.listInactive(
          companyRole,
          companyRoleValue,
          { search: search.value, ...columnFilters.value, page_size: EXPORT_EMAIL_THRESHOLD }
        )
        const { count = 0, results: items = [] } = data || {}

        if (!count) {
          showToast('No records available to export.')
          return
        }

        if (count > EXPORT_EMAIL_THRESHOLD) {
          await exportByEmail()
        } else {
          await downloadLocal(items)
        }
      })

      isExporting.value = false
    }

    return { isExporting, handleExport }
  }
})
</script>

<style scoped>
.bdx-export-icon-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: var(--primary);
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
}
.bdx-export-icon-btn:hover { opacity: 0.85; }
.bdx-export-icon-btn[data-exporting] { cursor: not-allowed; opacity: 0.75; }

.bdx-btn-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bdx-spin 0.7s linear infinite;
}
@keyframes bdx-spin { to { transform: rotate(360deg); } }
</style>
